import { useEffect } from 'react';

/**
 * Reveals elements as they scroll into view.
 *
 * Elements opt in with a `data-reveal` attribute; an optional
 * `data-reveal-delay` (in ms) staggers siblings:
 *
 *   <div data-reveal>…</div>
 *   <div data-reveal data-reveal-delay="80">…</div>
 *
 * Design notes
 * ------------
 * - One observer for the whole page, not one per component.
 * - Elements are revealed once and then unobserved — re-animating on the way
 *   back up is distracting and costs work on every scroll.
 * - The hidden state is applied by JS (`data-reveal-ready`), never by the
 *   stylesheet alone, so with JS disabled or on error everything is visible.
 *   Content must never depend on script to be readable.
 * - Under `prefers-reduced-motion` nothing is hidden in the first place.
 */
export const useScrollReveal = () => {
  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll('[data-reveal]'));
    if (nodes.length === 0) return undefined;

    const prefersReduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      nodes.forEach(node => node.setAttribute('data-reveal', 'in'));
      return undefined;
    }

    // Arm the hidden state only once we know we can animate out of it.
    nodes.forEach(node => {
      node.setAttribute('data-reveal-ready', '');
      const delay = node.getAttribute('data-reveal-delay');
      if (delay) node.style.transitionDelay = `${delay}ms`;
    });

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.setAttribute('data-reveal', 'in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    nodes.forEach(node => observer.observe(node));
    return () => observer.disconnect();
  }, []);
};

export default useScrollReveal;
